(function () {
  if (customElements.get('welcome-offer')) return;

  var STORAGE_KEY = 'cgs_welcome_offer_v1';

  /**
   * Placeholder shown when no real code can be issued (theme editor, or the
   * endpoint setting is empty). It is NEVER a real discount, so it must never
   * be persisted and must never be routed through /discount/<code> - Shopify
   * errors on an unknown code, which is exactly what dead-ended the preview.
   */
  var PREVIEW_CODE = 'PREVIEW-ONLY';

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      return null;
    }
  }

  function writeState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  function normalizeEgyptianMobile(raw) {
    var d = String(raw || '').replace(/\D/g, '');
    if (d.indexOf('0020') === 0) d = d.slice(4);
    else if (d.indexOf('20') === 0 && d.length >= 12) d = d.slice(2);
    if (d.indexOf('0') === 0) d = d.slice(1);
    if (!/^1[0125]\d{8}$/.test(d)) return null;
    return '+20' + d;
  }

  function formatRemaining(ms) {
    if (ms < 0) ms = 0;
    var total = Math.floor(ms / 1000);
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    var pad = function (n) { return n < 10 ? '0' + n : String(n); };
    return pad(h) + ':' + pad(m) + ':' + pad(s);
  }

  function onIdle(fn) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(fn, { timeout: 3000 });
    } else {
      setTimeout(fn, 1200);
    }
  }

  class WelcomeOffer extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.designMode = this.dataset.designMode === 'true';
      this.endpoint = this.dataset.endpoint || '';
      this.hours = parseInt(this.dataset.hours, 10) || 24;
      this.reappearDays = parseInt(this.dataset.reappearDays, 10) || 30;
      this.stickyEnabled = this.dataset.stickyBar === 'true';
      this.redirect = this.dataset.redirect || '/collections/all';
      // 2026-10-10: Arabic shoppers were sent to the English catalogue. Keep them in /ar.
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      if (root !== '/' && this.redirect.charAt(0) === '/' && this.redirect.indexOf(root) !== 0) {
        this.redirect = root.replace(/\/$/, '') + this.redirect;
      }
      this.minSubtotal = parseFloat(this.dataset.min) || 0;
      this.amount = parseFloat(this.dataset.amount) || 0;
      this.isAr = /^ar/i.test(document.documentElement.lang || '');
      this.fallbackCode = (this.dataset.fallbackCode || '').trim();
      this.codeIsPreview = false;

      this.scrim = this.querySelector('[data-wo-scrim]');
      this.dialog = this.querySelector('[data-wo-dialog]');
      this.bar = this.querySelector('[data-wo-bar]');
      this.form = this.querySelector('[data-wo-form]');
      this.phoneInput = this.querySelector('[data-wo-phone]');
      this.consentInput = this.querySelector('[data-wo-consent]');
      this.errorEl = this.querySelector('[data-wo-error]');
      this.submitBtn = this.querySelector('[data-wo-submit]');
      this.spinner = this.querySelector('[data-wo-spinner]');
      this.stepForm = this.querySelector('[data-wo-step="form"]');
      this.stepSuccess = this.querySelector('[data-wo-step="success"]');
      this.previewNoteEl = this.querySelector('[data-wo-preview-note]');

      this.bindEvents();

      var forced = /[?&]wo=preview/.test(window.location.search);

      if (this.designMode || forced) {
        this.open();
        return;
      }

      onIdle(this.boot.bind(this));
    }

    boot() {
      if (this.isSuppressedContext()) return;

      var state = readState();
      var now = Date.now();

      if (state && state.code && state.expiresAt) {
        if (state.expiresAt > now) {
          this.code = state.code;
          this.expiresAt = state.expiresAt;
          // Only real codes are ever persisted, so anything restored is real.
          this.codeIsPreview = false;
          if (this.stickyEnabled && !state.barClosed) this.showBar();
          this.startCountdown();
        } else if (!state.expiredHandled) {
          // Claimed earlier, already expired, and we haven't cleaned up yet.
          this.code = state.code;
          this.releaseExpiredCode();
        }
        return;
      }

      if (state && state.dismissedAt) {
        var cooldown = this.reappearDays * 86400000;
        if (now - state.dismissedAt < cooldown) return;
      }

      this.armTriggers();
    }

    /**
     * Shopify keeps an expired code attached to the cart and only drops it at
     * checkout, so the cart can show a discount the buyer no longer has. That
     * reads as a price rise at checkout. When our code expires we detach it so
     * the cart tells the truth immediately.
     *
     * Deliberately conservative: we only clear when OUR code is present, is
     * flagged applicable:false, and no other applicable code would be
     * destroyed - /cart/update.js clears every code at once, so we never run it
     * while a valid one is attached.
     */
    releaseExpiredCode() {
      var self = this;
      if (this._expiryHandled) return;
      this._expiryHandled = true;

      var mark = function () {
        var s = readState() || {};
        s.expiredHandled = true;
        writeState(s);
      };

      if (!this.code) {
        mark();
        return;
      }

      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var codes = (cart && cart.discount_codes) || [];
          var mine = codes.filter(function (c) {
            return String(c.code).toUpperCase() === String(self.code).toUpperCase();
          });

          // Not on the cart, or somehow still valid - leave everything alone.
          if (!mine.length) { mark(); return null; }
          if (mine.some(function (c) { return c.applicable; })) return null;

          // Another code is doing real work - don't wipe it out.
          var otherApplicable = codes.filter(function (c) {
            return c.applicable && String(c.code).toUpperCase() !== String(self.code).toUpperCase();
          });
          if (otherApplicable.length) { mark(); return null; }

          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: '' })
          }).then(function () {
            mark();
            if (/\/cart(\/|$)/.test(window.location.pathname)) window.location.reload();
          });
        })
        .catch(function () { /* never break the page over cleanup */ });
    }

    isSuppressedContext() {
      var path = window.location.pathname;
      // `checkouts` (plural) is the real Shopify checkout path - /checkouts/c/<token>.
      if (/\/(cart|checkouts?)(\/|$)/.test(path)) return true;
      if (/[?&]discount=/.test(window.location.search)) return true;
      return false;
    }

    armTriggers() {
      var self = this;
      var isMobile = window.matchMedia('(max-width: 749px)').matches;
      var fired = false;
      var timerId;
      var onScroll = null;
      var onExit = null;

      var cleanup = function () {
        clearTimeout(timerId);
        if (onScroll) window.removeEventListener('scroll', onScroll);
        if (onExit) document.removeEventListener('mouseout', onExit);
      };

      var fire = function () {
        if (fired) return;
        fired = true;
        cleanup();
        self.open();
      };

      if (isMobile) {
        var delay = (parseInt(this.dataset.delayMobile, 10) || 20) * 1000;
        var threshold = (parseInt(this.dataset.scrollMobile, 10) || 45) / 100;
        timerId = setTimeout(fire, delay);

        onScroll = function () {
          var doc = document.documentElement;
          var scrollable = doc.scrollHeight - window.innerHeight;
          if (scrollable <= 0) return;
          if (window.scrollY / scrollable >= threshold) fire();
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      } else {
        var delayD = (parseInt(this.dataset.delayDesktop, 10) || 15) * 1000;
        timerId = setTimeout(fire, delayD);

        if (this.dataset.exitIntent === 'true') {
          onExit = function (e) {
            if (e.clientY <= 0 && !e.relatedTarget) fire();
          };
          document.addEventListener('mouseout', onExit);
        }
      }
    }

    bindEvents() {
      var self = this;

      this.querySelectorAll('[data-wo-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.dismiss(); });
      });

      if (this.scrim) {
        this.scrim.addEventListener('click', function () { self.dismiss(); });
      }

      this._onKeydown = function (e) {
        if (e.key === 'Escape') {
          self.dismiss();
          return;
        }
        if (e.key === 'Tab') self.trapFocus(e);
      };

      if (this.form) {
        this.form.addEventListener('submit', function (e) {
          e.preventDefault();
          self.submit();
        });
      }

      if (this.phoneInput) {
        this.phoneInput.addEventListener('input', function () {
          self.clearError();
        });
      }

      var copyMain = this.querySelector('[data-wo-code-copy]');
      if (copyMain) {
        copyMain.addEventListener('click', function () {
          self.copyCode(self.querySelector('[data-wo-copy-hint]'));
        });
      }

      var copyBar = this.querySelector('[data-wo-bar-code-copy]');
      if (copyBar) {
        copyBar.addEventListener('click', function () { self.copyCode(null); });
      }

      var barClose = this.querySelector('[data-wo-bar-close]');
      if (barClose) {
        barClose.addEventListener('click', function () {
          self.hideBar();
          var state = readState() || {};
          state.barClosed = true;
          writeState(state);
        });
      }
    }

    open() {
      if (!this.dialog || !this.scrim) return;
      this.lastFocused = document.activeElement;

      this.scrim.hidden = false;
      this.dialog.hidden = false;
      void this.dialog.offsetWidth;
      this.scrim.setAttribute('data-open', '');
      this.dialog.setAttribute('data-open', '');

      document.addEventListener('keydown', this._onKeydown);

      var input = this.phoneInput;
      var closeBtn = this.dialog.querySelector('[data-wo-close]');
      if (input && window.matchMedia('(min-width: 750px)').matches) {
        setTimeout(function () { input.focus(); }, 420);
      } else if (closeBtn) {
        setTimeout(function () { closeBtn.focus({ preventScroll: true }); }, 420);
      }
    }

    close() {
      if (!this.dialog || !this.scrim) return;
      this.scrim.removeAttribute('data-open');
      this.dialog.removeAttribute('data-open');
      document.removeEventListener('keydown', this._onKeydown);

      var self = this;
      setTimeout(function () {
        self.scrim.hidden = true;
        self.dialog.hidden = true;
      }, 420);

      if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
    }

    dismiss() {
      this.close();
      if (this.designMode) return;

      var state = readState() || {};
      if (!state.code) {
        state.dismissedAt = Date.now();
        writeState(state);
      } else if (this.stickyEnabled && !state.barClosed) {
        this.showBar();
      }
    }

    trapFocus(e) {
      var focusables = this.dialog.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      var visible = Array.prototype.filter.call(focusables, function (el) {
        return el.offsetParent !== null && !el.disabled;
      });
      if (!visible.length) return;

      var first = visible[0];
      var last = visible[visible.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      var field = this.querySelector('.wo__field');
      if (field) field.setAttribute('data-invalid', '');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      var field = this.querySelector('.wo__field');
      if (field) field.removeAttribute('data-invalid');
    }

    setLoading(isLoading) {
      if (this.submitBtn) this.submitBtn.disabled = isLoading;
      if (this.spinner) this.spinner.hidden = !isLoading;
    }

    t(en, ar) {
      return this.isAr ? ar : en;
    }

    submit() {
      var self = this;
      this.clearError();

      var phone = normalizeEgyptianMobile(this.phoneInput ? this.phoneInput.value : '');
      if (!phone) {
        this.showError(this.t('Please enter a valid Egyptian mobile number.', 'اكتب رقم موبايل مصري صحيح.'));
        if (this.phoneInput) this.phoneInput.focus();
        return;
      }

      if (this.consentInput && !this.consentInput.checked) {
        this.showError(this.t('Please tick the box so we can send your code on WhatsApp.', 'علّم على المربع عشان نبعتلك الكود على واتساب.'));
        return;
      }

      if (this.designMode || !this.endpoint) {
        this.reveal(PREVIEW_CODE, Date.now() + this.hours * 3600000, true);
        return;
      }

      this.setLoading(true);

      fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: phone,
          consent: true,
          hours: this.hours,
          source: window.location.pathname
        })
      })
        .then(function (res) {
          if (res.status === 429) throw new Error('rate_limited');
          if (!res.ok) throw new Error('http_' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (!data || !data.code) throw new Error('no_code');
          var expiresAt = data.expires_at
            ? new Date(data.expires_at).getTime()
            : Date.now() + self.hours * 3600000;
          // 2026-10-10: the Worker returns the SAME code for a number that already
          // claimed one - including after it expired. Revealing that showed a dead
          // code at 00:00:00, which customers reported as "the code doesn't work".
          if (expiresAt <= Date.now()) {
            self.showError(self.t(
              'This number already claimed its welcome offer and the code has expired. One code per number.',
              'الرقم ده أخد كود الترحيب قبل كده والكود انتهى. كود واحد لكل رقم.'
            ));
            return;
          }
          self.reveal(data.code, expiresAt);
        })
        .catch(function (err) {
          if (err && err.message === 'rate_limited') {
            self.showError(self.t(
              'Too many tries from this network. Please try again later, or message us on WhatsApp.',
              'محاولات كتير من نفس الشبكة. جرّب تاني بعد شوية أو كلّمنا على واتساب.'
            ));
          } else if (self.fallbackCode) {
            self.reveal(self.fallbackCode, Date.now() + self.hours * 3600000);
          } else {
            self.showError(self.t('Something went wrong. Please try again in a moment.', 'حصلت مشكلة. جرّب تاني كمان شوية.'));
          }
        })
        .finally(function () {
          self.setLoading(false);
        });
    }

    /**
     * CTA target for a code.
     *
     * Real codes go through /discount/<code>?redirect=..., which is Shopify's
     * own apply-and-continue endpoint. A preview code has no matching discount,
     * so that endpoint returns an error page - send those straight to the
     * redirect target instead.
     */
    discountHref(code) {
      if (!code || this.codeIsPreview) return this.redirect;
      return '/discount/' + encodeURIComponent(code) +
        '?redirect=' + encodeURIComponent(this.redirect);
    }

    reveal(code, expiresAt, isPreview) {
      this.code = code;
      this.expiresAt = expiresAt;
      this.codeIsPreview = isPreview === true;
      this._expiryHandled = false;

      // Never persist a placeholder: a stored preview code would come back on
      // the next page load as a sticky bar pointing at a dead discount link.
      if (!this.codeIsPreview) {
        // min/amount travel with the code so assets/welcome-offer-cart.js can explain
        // the minimum in the cart on any page.
        writeState({ code: code, expiresAt: expiresAt, claimedAt: Date.now(), min: this.minSubtotal, amount: this.amount });
      }

      var codeEl = this.querySelector('[data-wo-code]');
      var barCodeEl = this.querySelector('[data-wo-bar-code]');
      if (codeEl) codeEl.textContent = code;
      if (barCodeEl) barCodeEl.textContent = code;

      if (this.previewNoteEl) this.previewNoteEl.hidden = !this.codeIsPreview;

      var href = this.discountHref(code);
      var shop = this.querySelector('[data-wo-shop]');
      var barShop = this.querySelector('[data-wo-bar-shop]');
      if (shop) shop.href = href;
      if (barShop) barShop.href = href;

      if (this.stepForm) this.stepForm.hidden = true;
      if (this.stepSuccess) this.stepSuccess.hidden = false;

      this.startCountdown();
    }

    startCountdown() {
      var self = this;
      var main = this.querySelector('[data-wo-countdown]');
      var bar = this.querySelector('[data-wo-bar-countdown]');

      var tick = function () {
        var remaining = self.expiresAt - Date.now();
        var text = formatRemaining(remaining);
        if (main) main.textContent = text;
        if (bar) bar.textContent = text;

        if (remaining <= 0) {
          clearInterval(self.countdownId);
          self.hideBar();
          if (!self.designMode && !self.codeIsPreview) self.releaseExpiredCode();
        }
      };

      clearInterval(this.countdownId);
      tick();
      this.countdownId = setInterval(tick, 1000);
    }

    showBar() {
      if (!this.bar || !this.stickyEnabled) return;

      var codeEl = this.querySelector('[data-wo-bar-code]');
      if (codeEl && this.code) codeEl.textContent = this.code;

      var barShop = this.querySelector('[data-wo-bar-shop]');
      if (barShop && this.code) barShop.href = this.discountHref(this.code);

      this.bar.hidden = false;
      void this.bar.offsetWidth;
      this.bar.setAttribute('data-open', '');
    }

    hideBar() {
      if (!this.bar) return;
      this.bar.removeAttribute('data-open');
      var self = this;
      setTimeout(function () { self.bar.hidden = true; }, 420);
    }

    copyCode(hintEl) {
      if (!this.code) return;
      var original = hintEl ? hintEl.textContent : null;

      var done = function () {
        if (!hintEl) return;
        hintEl.textContent = 'Copied';
        setTimeout(function () { hintEl.textContent = original; }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.code).then(done).catch(function () {});
      } else {
        var ta = document.createElement('textarea');
        ta.value = this.code;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); done(); } catch (e) {}
        document.body.removeChild(ta);
      }
    }

    disconnectedCallback() {
      clearInterval(this.countdownId);
      document.removeEventListener('keydown', this._onKeydown);
    }
  }

  customElements.define('welcome-offer', WelcomeOffer);
})();
